export interface BackgroundGradientConfig {
    active: boolean;
    fillPriority: 'color' | 'linear-gradient';
    startPoint?: { x: number; y: number };
    endPoint?: { x: number; y: number };
    colorStops?: Array<number | string>;
}

export type CanvasGradientAxis = 'none' | 'horizontal' | 'vertical';

/**
 * Resolve the firmware enum through Schema V2 metadata. The numeric values are
 * intentionally not interpreted by the canvas renderer itself.
 */
export function resolveGradientAxis(propertyDefinition: any, direction: number): CanvasGradientAxis {
    const option = propertyDefinition?.values?.find(
        (value: any) => Number(value?.value ?? value) === Number(direction),
    );
    const axis = option?.canvasAxis;
    return axis === 'horizontal' || axis === 'vertical' ? axis : 'none';
}

const clampStop = (value: number) => Math.max(0, Math.min(255, Number(value)));

function firmwareStops(
    axisSize: number,
    mainStop: number,
    gradientStop: number,
    mainColor: string,
    gradientColor: string,
) {
    // LVGL 7.11 samples integer coordinates 0..size-1. grad_get() computes
    // floor(stop * size / 256), then floor((grad-main)*size/256) for the
    // interpolation denominator. Position stops at pixel centers so the
    // continuous canvas gradient follows those sampled coordinates.
    const size = Math.max(1, Math.round(axisSize));
    const main = clampStop(mainStop);
    const gradient = clampStop(gradientStop);
    const min = Math.floor((main * size) / 256);
    const max = Math.floor((gradient * size) / 256);
    const mainOffset = Math.min(1, Math.max(0, (min + 0.5) / size));

    // grad_get() checks i <= min before i >= max. Equal or descending stops
    // therefore make a hard main->gradient edge immediately after min; they
    // do not reverse colors or interpolate between reordered stops.
    if (main >= gradient || min === max) {
        const edge = Math.min(1, Math.max(0, (min + 1) / size));
        return [edge, mainColor, edge, gradientColor];
    }

    const distance = Math.floor((size * (gradient - main)) / 256);
    if (distance <= 0) {
        const edge = Math.min(1, Math.max(0, (min + 1) / size));
        return [edge, mainColor, edge, gradientColor];
    }

    const gradientOffset = Math.min(1, Math.max(0, (min + 0.5 + distance) / size));
    // Due to independent integer truncation, `max` can be one coordinate past
    // `min + distance`; at min+distance LVGL's mix has already reached color 2.
    return [mainOffset, mainColor, gradientOffset, gradientColor];
}

/**
 * Translate a Schema V2-resolved axis and actual LVGL 7.11 stop semantics to
 * Konva's native gradient attributes. LVGL uses stop*size/256, not stop/255;
 * its integer coordinate thresholds also mean the pixel dimensions matter.
 */
export function resolveBackgroundGradient(
    axis: CanvasGradientAxis,
    mainColor: string,
    gradientColor: string | undefined,
    mainStop: number,
    gradientStop: number,
    width: number,
    height: number,
): BackgroundGradientConfig {
    if (axis === 'none') {
        return { active: false, fillPriority: 'color' };
    }

    const axisSize = axis === 'horizontal' ? width : height;
    const colorStops = firmwareStops(axisSize, mainStop, gradientStop, mainColor, gradientColor ?? mainColor);

    return {
        active: true,
        fillPriority: 'linear-gradient',
        startPoint: { x: 0, y: 0 },
        endPoint: axis === 'horizontal' ? { x: width, y: 0 } : { x: 0, y: height },
        colorStops,
    };
}
